import axios, { AxiosError, AxiosInstance } from 'axios';
import { apiTrackingService } from './apiTrackingService';
import { API_URL } from '../config/env';
import { logger } from '../utils/logger';
import { offlineQueueService } from './offlineQueueService';
import { clearBackendSession, supabase } from './supabase';
import { clearCsrfToken, getCsrfToken } from './csrf';
import type { GroupPayload, ProfileUpdatePayload } from '@vitly/shared';
import {
  groupPayloadSchema,
  profileUpdateSchema,
  meetupCreateSchema,
  guestInviteSchema,
  subscriptionCreateSchema,
  subscriptionCancelSchema,
} from '@vitly/shared';

export {
  groupPayloadSchema,
  profileUpdateSchema,
  meetupCreateSchema,
  guestInviteSchema,
  subscriptionCreateSchema,
  subscriptionCancelSchema,
};

interface QueuedResponse {
  data: {
    queued: true;
    message: string;
  };
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: unknown;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.prewarmCsrfToken();

    this.client.interceptors.request.use(
      async (config) => {
        if (import.meta.env.DEV) {
          logger.debug('[API REQUEST]', config.method?.toUpperCase(), config.url);
        }

        const method = (config.method || 'get').toLowerCase();
        const isSafeMethod = method === 'get' || method === 'head' || method === 'options';

        if (!isSafeMethod) {
          const csrfToken = await this.getCachedCsrfToken();
          config.headers = config.headers || {};
          config.headers['X-CSRF-Token'] = csrfToken;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response) => {
        if (import.meta.env.DEV) {
          logger.debug(
            '[API RESPONSE]',
            response.config.method?.toUpperCase(),
            response.config.url,
            'Status:',
            response.status,
          );
        }
        return response;
      },
      async (error: AxiosError) => {
        if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
          return Promise.reject(error);
        }

        const queuedResponse = this.queueOfflineRequest(error);
        if (queuedResponse) {
          return Promise.resolve(queuedResponse);
        }

        logger.error(
          '[API ERROR]',
          error.config?.method?.toUpperCase(),
          error.config?.url,
          'Status:',
          error.response?.status,
        );
        logger.error('[API ERROR] Details:', error.response?.data);

        const originalRequest = error.config as any;

        if (error.response?.status === 403) {
          const responseData: any = error.response.data;
          const message =
            typeof responseData === 'string'
              ? responseData
              : responseData?.error || responseData?.message;

          if (message && message.toLowerCase().includes('csrf')) {
            clearCsrfToken();

            if (!originalRequest?._csrfRetry) {
              originalRequest._csrfRetry = true;
              try {
                const csrfToken = await getCsrfToken();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['X-CSRF-Token'] = csrfToken;
                return this.client.request(originalRequest);
              } catch (tokenError) {
                logger.error('[API] Failed to refresh CSRF token:', tokenError);
              }
            }
          }
        }

        if (error.response?.status === 401) {
          const publicEndpoints = ['/auth/', '/push/vapid-public-key', '/health'];
          const isPublicEndpoint = publicEndpoints.some((endpoint) =>
            originalRequest?.url?.includes(endpoint),
          );

          const PUBLIC_PATHS = [
            '/login',
            '/register',
            '/',
            '/guest',
            '/public',
            '/how-it-works',
            '/features',
            '/about',
            '/contact',
            '/blog',
            '/pricing',
            '/invitation',
            '/privacy',
            '/terms',
            '/data-deletion',
            '/help',
            '/community',
            '/status',
          ];
          const currentPath = window.location.pathname;
          const isPublicPage = PUBLIC_PATHS.some(
            (path) => currentPath === path || currentPath.startsWith(`${path}/`),
          );

          const responseData: any = error.response?.data;
          const errorMessage =
            typeof responseData === 'string'
              ? responseData
              : responseData?.error || responseData?.message || '';
          const errorLower = errorMessage.toLowerCase();
          const isTokenError =
            errorLower.includes('kid') ||
            errorLower.includes('jwt') ||
            errorLower.includes('unverifiable') ||
            errorLower.includes('invalid token') ||
            errorLower.includes('authentication failed');

          if (isTokenError && !originalRequest?._jwtCleared) {
            logger.warn('[API] Token error detected - clearing stale session');
            originalRequest._jwtCleared = true;

            try {
              await supabase.auth.signOut();
              await clearBackendSession();
            } catch (signOutError) {
              logger.error('[API] Error during forced signout:', signOutError);
            }

            try {
              const supabaseKeys = [
                'vitly-supabase-auth',
                'vitly-supabase-auth-yoplwihevhqmnugvgnwl',
                'vitly-supabase-auth-wgqbfukckbyctkbfopet',
                'sb-yoplwihevhqmnugvgnwl-auth-token',
                'sb-wgqbfukckbyctkbfopet-auth-token',
              ];
              supabaseKeys.forEach((key) => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
              });
              Object.keys(localStorage).forEach((key) => {
                if (key.startsWith('vitly-supabase-auth-')) {
                  localStorage.removeItem(key);
                }
              });

              document.cookie.split(';').forEach((cookie) => {
                const name = cookie.split('=')[0].trim();
                if (
                  name.includes('supabase') ||
                  name.includes('vitly') ||
                  name.includes('sb-') ||
                  name.includes('auth')
                ) {
                  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                  document.cookie = `${name}=; Path=/; Domain=${window.location.hostname}; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
                }
              });

              logger.info('[API] All Supabase/auth storage cleared');
            } catch (clearError) {
              logger.error('[API] Error clearing storage:', clearError);
            }

            if (!isPublicPage) {
              window.location.href = '/login';
              return Promise.reject(error);
            }
            return Promise.reject(error);
          }

          const isOptionalAuthCheck =
            originalRequest?.url?.includes('/auth/profile') ||
            originalRequest?.url?.includes('/auth/session');

          if (!isPublicPage && !isPublicEndpoint && !isOptionalAuthCheck) {
            logger.debug('[API] 401 received, redirecting to login (httpOnly cookie auth)');
            window.history.pushState({}, '', '/login');
            window.dispatchEvent(new CustomEvent('auth-redirect', { detail: { path: '/login' } }));
          }
        }

        apiTrackingService.trackAPICall(originalRequest?.url || '', 'ERROR');

        return Promise.reject(error);
      },
    );
  }

  get api() {
    return this.client;
  }

  get baseURL() {
    return API_URL;
  }

  private async prewarmCsrfToken(): Promise<void> {
    try {
      await getCsrfToken();
      logger.debug('[API] CSRF token prewarmed');
    } catch (error) {
      logger.warn('[API] Failed to prewarm CSRF token:', error);
    }
  }

  private async getCachedCsrfToken(): Promise<string> {
    try {
      return await getCsrfToken();
    } catch (error) {
      logger.warn('[API] CSRF token fetch failed, refreshing:', error);
      clearCsrfToken();
      return await getCsrfToken();
    }
  }

  private queueOfflineRequest(error: AxiosError): QueuedResponse | null {
    if (typeof navigator === 'undefined' || navigator.onLine || error.response) {
      return null;
    }

    const originalRequest = error.config as
      | {
          baseURL?: string;
          url?: string;
          method?: string;
          data?: unknown;
          headers?: Record<string, unknown>;
        }
      | undefined;

    if (!originalRequest) {
      return null;
    }

    const method = (originalRequest.method || '').toLowerCase();
    if (!['post', 'put', 'delete'].includes(method)) {
      return null;
    }

    if (originalRequest.url?.includes('/auth/')) {
      return null;
    }

    const contentType = this.getHeaderValue(originalRequest.headers, 'Content-Type');
    if (contentType?.includes('multipart/form-data')) {
      return null;
    }

    const upperMethod = method.toUpperCase() as 'POST' | 'PUT' | 'DELETE';
    const fullUrl = `${originalRequest.baseURL || ''}${originalRequest.url || ''}`;
    offlineQueueService.enqueue({
      method: upperMethod,
      url: fullUrl,
      data: this.parseRequestData(originalRequest.data),
      headers: { 'Content-Type': contentType || 'application/json' },
    });

    logger.info('[API] Request queued for offline retry', upperMethod, originalRequest.url);

    return {
      data: { queued: true, message: 'Request saved for offline retry' },
      status: 202,
      statusText: 'Accepted (Queued)',
      headers: {},
      config: originalRequest,
    };
  }

  private parseRequestData(data: unknown): unknown {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }

    return data;
  }

  private getHeaderValue(
    headers: Record<string, unknown> | undefined,
    name: string,
  ): string | undefined {
    if (!headers) {
      return undefined;
    }

    const exactValue = headers[name];
    if (typeof exactValue === 'string') {
      return exactValue;
    }

    const lowerName = name.toLowerCase();
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === lowerName && typeof value === 'string') {
        return value;
      }
    }

    return undefined;
  }
}

const apiClient = new ApiClient();
export default apiClient;

export { meetupApi } from './meetupApi';
export { friendsApi } from './friendsApi';
export { notificationApi } from './notificationApi';
export { suggestionApi } from './suggestionApi';

export const authApi = {
  login: async (email: string, password: string) =>
    apiClient.api.post('/v1/auth/login', { email, password }).then((res) => res.data),
  register: async (data: {
    email: string;
    password: string;
    username?: string;
    firstName?: string;
    lastName?: string;
  }) => apiClient.api.post('/v1/auth/register', data).then((res) => res.data),
  logout: async () => apiClient.api.post('/v1/auth/logout').then((res) => res.data),
  getProfile: async () => apiClient.api.get('/v1/users/profile').then((res) => res.data),
  updateProfile: async (data: ProfileUpdatePayload) =>
    apiClient.api.put('/v1/users/profile', data).then((res) => res.data),
  forgotPassword: async (email: string) =>
    apiClient.api.post('/v1/auth/forgot-password', { email }).then((res) => res.data),
  refreshToken: async () => apiClient.api.post('/v1/auth/refresh').then((res) => res.data),
  updatePassword: async (currentPassword: string, newPassword: string) =>
    apiClient.api.post('/v1/users/change-password', { currentPassword, newPassword }),
  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return apiClient.api
      .post('/v1/avatar/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data?.data || res.data);
  },
  deleteAvatar: async () => apiClient.api.delete('/v1/avatar'),
};

export const userApi = {
  getProfile: async () => apiClient.api.get('/v1/users/profile'),
  getPublicProfile: async (userId: string) => apiClient.api.get(`/v1/users/profile/${userId}`),
  updateProfile: async (data: ProfileUpdatePayload) => apiClient.api.put('/v1/users/profile', data),
  deleteAccount: async (userId: string) => apiClient.api.delete(`/v1/users/${userId}`),
  getVisibilitySettings: async () => apiClient.api.get('/v1/users/visibility'),
  updateVisibilitySettings: async (data: { basicInfo?: string; interests?: string; location?: string }) =>
    apiClient.api.put('/v1/users/visibility', data),
};

export const groupApi = {
  getGroups: async () => apiClient.api.get('/v1/groups'),
  createGroup: async (data: GroupPayload) => apiClient.api.post('/v1/groups', data),
  updateGroup: async (groupId: string, data: Partial<GroupPayload>) =>
    apiClient.api.put(`/v1/groups/${groupId}`, data),
  deleteGroup: async (groupId: string) => apiClient.api.delete(`/v1/groups/${groupId}`),
};
